import { Heading, Icon } from '@dittolive/anvil'
import { XIcon } from '@phosphor-icons/react'
import React, { useEffect, useState } from 'react'

import {
  ConnectorInitialSyncStatus,
  ConnectorStatus,
  getConnectionStringValue,
  MongoConnectorLink,
} from '../../../types/mongoConnector'
import MongoConnectorInitialSyncStatusTable from '../components/MongoConnectorInitialSyncStatus'
import MongoConnectorStatusBanner from '../components/MongoConnectorStatus'
import SyncedCollection from '../components/SyncedCollection'

type Props = {
  /** The MongoDB connector link data */
  link: MongoConnectorLink
  /** IP addresses that the connector uses to connect */
  clusterIps?: string[]
  /** Real-time connector status */
  status: ConnectorStatus | null
  /** Whether status is still loading */
  isStatusLoading?: boolean
  /** Initial sync statuses for collections */
  initialSyncStatuses?: ConnectorInitialSyncStatus[]
  /** Whether initial sync statuses are still loading */
  isInitialSyncLoading?: boolean
  /** Optional slot for documentation link */
  docsLink?: React.ReactNode
}

export default function MongoConnectorDetails({
  link,
  clusterIps,
  status,
  isStatusLoading,
  initialSyncStatuses = [],
  isInitialSyncLoading,
  docsLink,
}: Props) {
  const [connectionURL, setConnectionURL] = useState<string | null>(null)

  useEffect(() => {
    const connStr = getConnectionStringValue(link.connectionString)
    try {
      const url = new URL(connStr)
      setConnectionURL(url.host)
    } catch {
      // If failed to parse, the full connection string will be displayed.
      // This is already masked in the backend, so it's safe to display here.
      setConnectionURL(connStr)
    }
  }, [link.connectionString])

  const collections = link.collections || []

  return (
    <>
      <div className="flex flex-col gap-y-2">
        <div>
          <Heading level={3}>Connection info</Heading>
          <p className="text-foreground-subtle text-base">
            Details of the MongoDB database you are syncing with
          </p>
        </div>
        <div className="border-border-normal rounded-xl border p-4">
          <MongoConnectorStatusBanner
            status={status}
            isLoading={isStatusLoading}
          />

          <div className="flex flex-col gap-y-4">
            {connectionURL && (
              <div className="flex flex-1 flex-col space-y-1">
                <p className="text-foreground-subtle text-sm leading-4">
                  Cluster endpoint
                </p>
                <p
                  className="text-foreground-normal text-base font-medium leading-5"
                  data-testid="endpoint"
                >
                  {connectionURL}
                </p>
              </div>
            )}

            <div className="flex flex-1 flex-col space-y-1">
              <p className="text-foreground-subtle text-sm leading-4">
                Target database
              </p>
              <p
                className="text-foreground-normal text-base font-medium leading-5"
                data-testid="database"
              >
                {link.database}
              </p>
            </div>

            {!!clusterIps?.length && (
              <div className="flex flex-1 flex-col space-y-1">
                <p className="text-foreground-subtle text-sm leading-4">
                  Connection IP addresses
                </p>
                <div
                  className="text-foreground-normal text-base font-medium leading-5"
                  data-testid="connectionIps"
                >
                  {clusterIps.map((ip) => (
                    <div key={ip}>{ip}</div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <MongoConnectorInitialSyncStatusTable
        statuses={initialSyncStatuses}
        isLoading={isInitialSyncLoading}
      />

      <div className="flex flex-col gap-y-2">
        <div>
          <Heading level={3}>Collections</Heading>
          <p className="text-foreground-subtle text-base">
            The MongoDB collections you are syncing with Ditto. {docsLink}
          </p>
        </div>

        <div className="divide-border-normal border-border-normal flex flex-col divide-y rounded-lg border">
          {collections.map((collection, index) => (
            <SyncedCollection
              key={`collection-${index}-${collection.name}`}
              collection={collection}
              action={
                <div className="flex items-center gap-2">
                  {!collection.initialSync ? (
                    <div className="bg-fill-warning-secondary text-fill-warning inline-flex items-center rounded-md px-1.5 py-0.5 text-sm font-medium">
                      <Icon svg={<XIcon />} className="mr-1 size-3" />
                      <span>Initial sync</span>
                    </div>
                  ) : null}

                  {collection.mode === 'EJSON' ? (
                    <div className="bg-fill-info-secondary text-fill-info inline-flex items-center rounded-md px-1.5 py-0.5 text-sm font-medium">
                      <span>Extended JSON</span>
                    </div>
                  ) : null}
                </div>
              }
            />
          ))}
        </div>
      </div>
    </>
  )
}
